import { mkFetcher } from '../../caching/fetcher';
import { Maybe } from '../../types';
import { fetchJson } from '../../utils/fetch';
import URLSearchParamsUtils from '../../utils/URLSearchParams';
import {
  assertPathContent,
  assertRequestObject,
  assertStaticContent
} from './assertions';
import {
  getContentParamsFromConfig,
  mkT3PrivacyParams,
  t3BaseUrl,
  t3ContentParamsConfig,
  t3RequestOptions,
  t3StaticParamsConfig
} from './config';
import {
  PrivacyInfo,
  Typo3PathContent,
  Typo3PathContentResponse,
  Typo3Response,
  Typo3StaticContent,
  Typo3StaticContentResponse
} from './types';
import { T3Config } from './types/configs';
import { extractPathContent, extractStaticContent } from './utils';

declare global {
  interface Window {
    data: Maybe<Typo3StaticContentResponse & Typo3PathContentResponse>;
  }
}

export const fetchTypo3Content = async <Config extends T3Config>(
  config: Config,
  additionalParams: URLSearchParams = new URLSearchParams(),
  path: string = '/index.php'
) => {
  const json = await fetchJson<Typo3Response<Config>>(
    `${t3BaseUrl}${path}?${URLSearchParamsUtils.merge(
      getContentParamsFromConfig(config),
      additionalParams
    ).toString()}`,
    t3RequestOptions
  );
  assertRequestObject(config, json.request);
  return json;
};

export interface FetchPathContentArgs {
  path: string;
  additionalParams?: URLSearchParams;
}

export const fetchPathContent = async ({
  path,
  additionalParams = new URLSearchParams()
}: FetchPathContentArgs): Promise<Typo3PathContent> => {
  const typoPath = path === '/' ? '/index.php' : path;

  // Only use inline content if it matches the requested content
  const json: Typo3PathContentResponse = window.data && window.data.meta.page.link === path
    ? window.data
    : await fetchTypo3Content(
      t3ContentParamsConfig,
      additionalParams,
      typoPath
    )
  ;
  assertPathContent(json);
  return extractPathContent(json);
};

export interface FetchStaticContentArgs {
  languageUid?: number;
  additionalParams?: URLSearchParams;
}

export const fetchStaticContent = async ({
  languageUid = 0,
  additionalParams = new URLSearchParams()
}: FetchStaticContentArgs): Promise<Typo3StaticContent> => {

  // Only use inline content if it matches the requested content
  const json: Typo3StaticContentResponse = window.data && window.data.meta.sys_language_uid === languageUid
    ? window.data
    : await fetchTypo3Content(
      t3StaticParamsConfig,
      URLSearchParamsUtils.merge(
        additionalParams,
        URLSearchParamsUtils.from({
          L: languageUid
        })
      )
    )
  ;
  assertStaticContent(json);
  return extractStaticContent(json, languageUid);
};

export interface FetchPrivacyInfoArgs {
  path: string;
  searchValue: string;
}

export const fetchPrivacyInfo = async ({
  path,
  searchValue
}: FetchPrivacyInfoArgs): Promise<PrivacyInfo> => {
  const result = await fetchJson<PrivacyInfo>(
    `${t3BaseUrl}${path}?${mkT3PrivacyParams(searchValue).toString()}`,
    t3RequestOptions
  );
  return result;
};

export interface FetchContentArgs
  extends FetchPathContentArgs,
    FetchStaticContentArgs {}

export const fetchPathContentCached = mkFetcher<
  FetchPathContentArgs,
  Typo3PathContent
>({
  fetcher: fetchPathContent,
  hasher: ({ path, additionalParams }: FetchPathContentArgs) =>
    `Typo3.fetchPathContent('${path}', '${
      additionalParams ? additionalParams.toString() : ''
    }')`,
});

export const fetchStaticContentCached = mkFetcher<
  FetchStaticContentArgs,
  Typo3StaticContent
>({
  fetcher: fetchStaticContent,
  hasher: ({ languageUid, additionalParams }: FetchStaticContentArgs) =>
    `Typo3.fetchStaticContent('${String(languageUid)}', '${
      additionalParams ? additionalParams.toString() : ''
    }')`,
});

export const fetchPrivacyInfoCached = mkFetcher<FetchPrivacyInfoArgs, string>({
  fetcher: fetchPrivacyInfo,
  hasher: ({ path, searchValue }) =>
    `Typo3.fetchPrivacyInfo('${path}', '${searchValue}')`,
});
